import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Cta } from '../Cta';
import { Reveal } from '../Reveal';
import { useScore } from '../ScoreContext';
const earlyStages = ['Helps your readers apply your ideas to their situation', 'Gives them a useful next move', 'Shows you what they need'];

/**
 * Path Author Leverage builds. Final stage is the endpoint; services are examples.
 */
function ReaderToWorkSpine() {
  const reduceMotion = useReducedMotion();
  return <div className="relative mx-auto w-fit max-w-full text-left lg:mx-0 lg:w-full lg:max-w-none" data-id="element-772" data-mp="1sem7tqoc4wyc">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-773" data-mp="1xc5mgroy3bqt">
        We build the path that
      </p>

      {/* Line terminates at the final stage marker — not beside the examples */}
      <div className="relative mt-4 lg:mt-5" data-id="element-774" data-mp="14o0060i8teov">
        <div className="absolute bottom-0 left-[0.4375rem] top-[0.7rem] w-px bg-gradient-to-b from-seaglass from-[65%] to-sand" aria-hidden="true" data-id="element-775" data-mp="19mtcvqpph1nz" />

        <ol className="relative space-y-4 lg:space-y-5" data-id="element-776" data-mp="10pwjetlzhikv">
          {earlyStages.map((label, i) => <motion.li key={label} className="grid grid-cols-[0.875rem_1fr] items-start gap-x-4" initial={reduceMotion ? false : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: reduceMotion ? 0 : 0.1 + i * 0.08,
          duration: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-777" data-mp="vs3gc9fz94ot" data-mpi={i}>
              <span className="relative z-10 mt-[0.55rem] h-2 w-2 justify-self-center rounded-full bg-seaglass ring-[3px] ring-paper" aria-hidden="true" data-id="element-778" data-mp="d027ce9hny1o" />
              <span className="font-serif text-lg leading-snug text-navy md:text-xl" data-id="element-779" data-mp="83tvb9ql8kyq">
                {label}
              </span>
            </motion.li>)}
        </ol>

        <motion.div className="relative mt-4 grid grid-cols-[0.875rem_1fr] items-start gap-x-4 lg:mt-5" initial={reduceMotion ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: reduceMotion ? 0 : 0.38,
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-780" data-mp="22fi6r1xm25ed">
          <span className="relative z-10 mt-[0.6rem] h-2.5 w-2.5 justify-self-center rounded-full bg-sand ring-[3px] ring-paper" aria-hidden="true" data-id="element-781" data-mp="3hzhptbim4zb1" />
          <div className="min-w-0" data-id="element-782" data-mp="3myc6o7x0sdsi">
            <p className="font-serif text-xl font-semibold leading-snug text-navy md:text-[1.35rem]" data-id="element-783" data-mp="eiiozpqycw9v">
              Connects them with the right way to work with you:
            </p>
            <p className="mt-2.5 text-base font-medium leading-[1.45] text-[#c89860]" data-id="element-784" data-mp="jezn9crv8r0o">
              <span className="md:hidden" data-id="element-785" data-mp="5jnjygzn9s85">
                Coaching · Consulting · Speaking
                <br data-id="element-786" data-mp="9hcxfv913f1x" />
                Training · Workshops · Courses
                <br data-id="element-787" data-mp="2q6qnqke90gdv" />
                Certification · Communities · and more
              </span>
              <span className="hidden md:inline" data-id="element-788" data-mp="2uag9z37galvq">
                Coaching · Consulting · Speaking · Training · Workshops
                <br data-id="element-789" data-mp="2mob6jwq7wini" />
                Courses · Certification · Communities · and more
              </span>
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}
function ScrollCue() {
  const reduceMotion = useReducedMotion();
  return <motion.div className="mt-10 flex justify-center md:mt-12" aria-hidden="true" animate={reduceMotion ? undefined : {
    y: [0, 4, 0]
  }} transition={reduceMotion ? undefined : {
    duration: 2.2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-790" data-mp="2rofmnln7rbd7">
      <ChevronDown className="h-5 w-5 text-seaglass/50" strokeWidth={1.75} data-id="element-791" data-mp="1z24la8rs96fc" />
    </motion.div>;
}
export function HeroV2() {
  const {
    openDiagnostic
  } = useScore();
  return <section className="relative z-10 bg-paper px-6 pb-14 pt-12 shadow-[0_14px_28px_-22px_rgba(26,40,56,0.32)] md:px-10 md:pb-16 md:pt-14" data-id="element-792" data-mp="28xpi7shzvgi8">
      <div className="mx-auto max-w-6xl" data-id="element-793" data-mp="1l6k35fjvos8z">
        <div className="mx-auto max-w-3xl text-center" data-id="element-794" data-mp="1galk87hbk2fy">
          <Reveal data-id="element-795" data-mp="1g7zco7ufhmxq">
            <span className="inline-block rounded-full border border-navy/12 bg-white px-4 py-1.5 text-[0.9375rem] font-medium leading-snug text-navy/90 shadow-sm md:text-base" data-id="element-796" data-mp="jiovrl2ic5iv">
              For expert-authors whose work goes beyond the book
            </span>
          </Reveal>
          <Reveal delay={0.04} data-id="element-797" data-mp="sha8gzlasqbq">
            <h1 className="mt-5 font-serif text-[clamp(2.35rem,5vw,4rem)] leading-[1.04] tracking-[-0.02em] text-navy" data-id="element-798" data-mp="xa3errwf06px">
              <span className="block" data-id="element-799" data-mp="1284vi66dbty8">You wrote the book.</span>
              <span className="mt-1.5 block italic text-[#2f4a42]" data-id="element-800" data-mp="177rd0rt8bwcu">
                We build what comes next.
              </span>
            </h1>
          </Reveal>
        </div>

        <div className="mx-auto mt-10 max-w-5xl border-t border-gray-200/70 pt-9 md:mt-12 md:pt-12" data-id="element-801" data-mp="1b7rwiqv6qxqr">
          <div className="grid gap-9 md:gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-12 xl:gap-14" data-id="element-802" data-mp="3d0zde4qg5ner">
            {/* Left conversion stack — contents on mobile so path can sit between */}
            <div className="contents lg:col-start-1 lg:flex lg:flex-col lg:gap-5 lg:self-center lg:text-left" data-id="element-803" data-mp="3agjia651xjqx">
              <Reveal delay={0.08} className="order-1 text-center lg:order-none lg:text-left" data-id="element-804" data-mp="35q35mo92225m">
                <p className="mx-auto max-w-xl text-lg font-semibold leading-snug text-navy md:text-xl lg:mx-0" data-id="element-805" data-mp="30qgs0dpbejwa">
                  Your book gives readers the ideas.
                </p>
                <p className="mx-auto mt-2.5 max-w-xl text-base leading-relaxed text-text-muted md:text-lg lg:mx-0" data-id="element-806" data-mp="2vpsquz7dgmoc">
                  We turn those ideas into a path your readers can use—
                  <br data-id="element-807" data-mp="3oe86nnybv90q" />
                  and you can keep using across your business.
                </p>
              </Reveal>

              <Reveal delay={0.12} className="order-3 flex flex-col items-center lg:order-none lg:items-start" data-id="element-808" data-mp="3ffwdbe62tx4l">
                <div className="flex w-full max-w-md flex-col items-stretch gap-2.5 sm:w-fit sm:max-w-full lg:max-w-none" data-id="element-809" data-mp="282b5pmim4muc">
                  <Cta variant="primary" onClick={openDiagnostic} className="min-h-[52px] px-6 py-3.5 text-base font-semibold md:text-lg" data-id="element-810" data-mp="23zobxg2dsrui">
                    Get Your Reader-to-Revenue Score
                  </Cta>
                  <p className="max-w-[16.5rem] self-center text-center text-base leading-snug text-navy/65" data-id="element-811" data-mp="2afy6e3fnliby">
                    8 questions to see how clearly reader interest connects to
                    your business.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Path — mobile order 2 (between ideas and CTA); desktop right column */}
            <Reveal delay={0.1} className="order-2 flex w-full justify-center lg:order-none lg:col-start-2 lg:row-start-1 lg:block lg:justify-start lg:self-center" data-id="element-812" data-mp="1rj6g3a1p22ye">
              <ReaderToWorkSpine data-id="element-813" data-mp="1hr9626mf9h19" />
            </Reveal>
          </div>

          <ScrollCue data-id="element-814" data-mp="13s9i716qh665" />
        </div>
      </div>
    </section>;
}